import React, { FunctionComponent } from 'react'
import { T } from '@transifex/react'
import VendNumber, { round } from 'vend-number'
import { Text } from '@lightspeed/design-system-react'
import { Currency } from '@vend/business-components'
import { CustomerBalances as CustomerBalancesModel } from '../../../models/sale'
import {
  useLoyaltyManager,
  useRetailerSettingsManager,
} from '../../../ng-compat/hooks'
import { useAppSelector } from '../../../state/hooks'
import {
  selectCustomerBalances,
  selectSaleCustomer,
} from '../../../state/slices/currentSale/currentSaleSlice'
import { customerBalancesManager } from '../../../managers/customer-balances-manager'
import { isValidCustomer } from '../../../functions/sale'

export const CustomerBalancesSection: FunctionComponent = () => {
  const saleCustomer = useAppSelector(selectSaleCustomer)
  const validCustomer = isValidCustomer(saleCustomer)
  const customerBalances = useAppSelector(selectCustomerBalances)
  const retailerSettingsManager = useRetailerSettingsManager()
  const loyaltyManager = useLoyaltyManager()
  const isStoreCreditEnabled = retailerSettingsManager.isStoreCreditEnabled()
  const isLoyaltyEnabled = retailerSettingsManager.isLoyaltyEnabled()

  const calculateCustomerLoyaltyBalance = () => {
    return loyaltyManager.getCustomerLoyaltyBalance()
  }

  const determineStoreCreditBalance = () => {
    const creditBalance = customerBalancesManager.calculateStoreCreditBalance()
    return round(creditBalance, 2, VendNumber.ROUNDING_MODES.ROUND_DOWN)
  }

  const getOnAccountLimitBalance = (balances: CustomerBalancesModel) => {
    return balances.availableOnAccountLimitBalance < 0
      ? 0
      : balances.availableOnAccountLimitBalance
  }

  return (
    <div className="wr-customer-badge-balances vd-mt0">
      {customerBalances && saleCustomer && validCustomer && (
        <ul className="wr-info-fields wr-info-fields--large wr-info-fields--center vd-mb3">
          <li className="wr-info-field">
            <div className="wr-info-field-label">
              <T
                _str="Account balance"
                _context="Customer balances account balance label"
              />
            </div>
            <div className="wr-info-field-value pro-customer-balances-amount--account">
              <Currency value={customerBalances.account} />
            </div>
            {customerBalances.onAccountLimit !== null ? (
              <Text type="supplementary" className="vd-pt1">
                <T
                  _str="On account limit available:"
                  _context="Customer balances account limit label"
                />
                &nbsp;
                <Currency value={getOnAccountLimitBalance(customerBalances)} />
              </Text>
            ) : null}
          </li>
          {isLoyaltyEnabled && (
            <li className="wr-info-field">
              <div className="wr-info-field-label">
                <T _str="Loyalty" _context="Customer balances loyalty label" />
              </div>
              <div className="wr-info-field-value">
                {saleCustomer.enable_loyalty && (
                  <div className="wr-customer-loyalty-balance">
                    <Currency value={calculateCustomerLoyaltyBalance()} />
                  </div>
                )}
                {!saleCustomer.enable_loyalty && (
                  <div className="wr-customer-loyalty-balance wr-customer-loyalty-balance--disabled">
                    <T
                      _str="Disabled"
                      _context="Customer balances disabled value"
                    />
                  </div>
                )}
              </div>
            </li>
          )}
          {isStoreCreditEnabled && (
            <li className="wr-info-field">
              <div className="wr-info-field-label">
                <T
                  _str="Store credit"
                  _context="Customer balances store credit label"
                />
              </div>
              <div className="wr-info-field-value">
                <div className="pro-customer-balances-amount--store-credit">
                  <Currency value={determineStoreCreditBalance()} />
                </div>
              </div>
            </li>
          )}
        </ul>
      )}
    </div>
  )
}
